import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  CdkDropListGroup,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { NgIf } from '@angular/common';
import { Component, computed, input, InputSignal, signal, Signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { get, groupBy } from 'lodash';
import { IDatatableItem } from '../../../pages/items/item-view/items-kanban/items-kanban.interface';
import { ItemsModalComponent } from '../../../pages/items/items-modal/items-modal.component';
import { EWorkflow, TStatusCategory } from '../../../pages/items/items.interface';
import { ItemsService } from '../../../pages/items/items.service';
import { ELoadStatus } from '../../../state/state.interface';
import { ItemCardComponent } from '../item-card/item-card.component';
import { IKanbanViewStore } from './kanban-view.store';

@Component({
  imports: [
    CdkDropListGroup,
    CdkDropList,
    CdkDrag,
    TranslatePipe,
    MatIcon,
    NgIf,
    ItemCardComponent,
    FormsModule,
    MatFormField,
    MatInput,
    MatLabel,
    MatSuffix,
  ],
  providers: [IKanbanViewStore],
  selector: 'app-kanban-view',
  styleUrl: './kanban-view.component.scss',
  templateUrl: './kanban-view.component.html',
})
export class KanbanViewComponent<Item extends IDatatableItem, GroupDefiner extends keyof Item> {
  public searchTerm = signal<string>('');
  public boardColumnIdPrefix = 'board-column-';
  public items: InputSignal<Item[]> = input.required<Item[]>();
  public groupDefiner: InputSignal<GroupDefiner> = input.required<GroupDefiner>();
  public kanbanColumns: InputSignal<EWorkflow[]> = input.required<EWorkflow[]>();
  public groups: Signal<Item[][]> = computed(() => {
    const search = this.searchTerm().toLowerCase();
    const filteredItems = this.items().filter((item) => item.title.toLowerCase().includes(search));

    const grouped: Record<string, Item[]> = groupBy(filteredItems, this.groupDefiner()) as Record<string, Item[]>;

    return this.kanbanColumns().map((category: EWorkflow): Item[] => grouped[category] ?? []);
  });

  constructor(
    public readonly store: IKanbanViewStore,
    private readonly translate: TranslateService,
    private readonly itemsService: ItemsService,
  ) {
    this.store.dataStatus$.pipe().subscribe((data: ELoadStatus) => {
      if (data === ELoadStatus.success || data === ELoadStatus.failure) {
        this.itemsService.triggerRefresh();
      }
    });

    setTimeout((): void => {
      this.calculateColumnHeight();
    }, 1000);
  }

  private calculateColumnHeight(): void {
    const columns: HTMLElement[] = Array.from(document.getElementsByClassName('scw-kanban-list')) as HTMLElement[];
    const maxHeight: number = Math.max(...columns.map((el: HTMLElement) => el?.offsetHeight));

    columns.forEach((el: HTMLElement): string => (el.style.minHeight = `${maxHeight ?? 500}px`));
  }

  public drop(event: CdkDragDrop<Item[]>): void {
    const isColumnChanged: boolean = event.previousContainer !== event.container;
    const currentItem: Item = event.item.data;
    const columnCategory: TStatusCategory = event.container.id.replace(this.boardColumnIdPrefix, '') as TStatusCategory;
    let previousItem: Item | undefined = get(event, `container.data.[${event.currentIndex - 1}]`);
    let nextItem: Item | undefined = get(event, `container.data.[${event.currentIndex}]`);

    if (!isColumnChanged) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);

      previousItem = get(event, `container.data.[${event.currentIndex - 1}]`);
      nextItem = get(event, `container.data.[${event.currentIndex + 1}]`);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }

    this.calculateColumnHeight();

    this.store.changeRange({
      columnCategory,
      itemId: currentItem.id,
      nextItemId: nextItem?.id || null,
      previousItemId: previousItem?.id || null,
    });
  }

  public openItemEditModal(itemId: number, itemKey: string, boardKey: string): void {
    this.itemsService.openItemEditDialogAndSetUrl(
      boardKey,
      itemId,
      itemKey,
      this.translate.instant('button.saveChanges'),
    );
  }

  public onSearch(event: KeyboardEvent): void {
    this.searchTerm.set(get(event, 'target.value', ''));
  }

  public triggerRefreshData(): void {
    this.itemsService.triggerRefresh();
  }
}
